import React, { useContext } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { signOut } from 'firebase/auth';
import { Link, useNavigate } from 'react-router-dom';
import './KnowYourRights.css';
import { LanguageContext } from '../App';
import { useAuth } from '../context/AuthContext';
import { FIRM_INFO } from '../constants/firmInfo';
import { auth, db } from '../firebase';
import { sendLeadToZapier } from '../utils/zapier';
import { translations } from '../utils/translations';

function KnowYourRights() {
  const navigate = useNavigate();
  const { language } = useContext(LanguageContext);
  const t = translations[language] || translations.en;
  const { user } = useAuth();
  const SCENARIOS = [
    {
      id: 1,
      title: t.dashboard.publicTitle,
      description: t.dashboard.publicDesc,
      route: '/public-stop',
    },
    {
      id: 2,
      title: t.dashboard.workTitle,
      description: t.dashboard.workDesc,
      route: '/workplace',
    },
    {
      id: 3,
      title: t.dashboard.homeTitle,
      description: t.dashboard.homeDesc,
      route: '/home',
    },
  ];

  const handleCallAttorney = async () => {
    const authUser = user || auth.currentUser;
    const fallbackLead = {
      name: authUser?.displayName || '',
      email: authUser?.email || '',
      phone: '',
      language,
      scenario: t.dashboard.contactAttorneyTitle,
      message: '',
    };

    let leadData = fallbackLead;

    if (authUser && db) {
      try {
        const [userDoc, intakeDoc] = await Promise.all([
          getDoc(doc(db, 'users', authUser.uid)),
          getDoc(doc(db, 'intake', authUser.uid)),
        ]);
        const userData = userDoc.exists() ? userDoc.data() : {};
        const intakeData = intakeDoc.exists() ? intakeDoc.data() : {};

        leadData = {
          name:
            userData.name ||
            intakeData.name ||
            fallbackLead.name,
          email:
            authUser.email ||
            userData.email ||
            intakeData.email ||
            fallbackLead.email,
          phone:
            userData.phone ||
            intakeData.phone ||
            '',
          language:
            intakeData.preferredLanguage ||
            userData.preferredLanguage ||
            language,
          scenario: t.dashboard.contactAttorneyTitle,
          message:
            intakeData.situationDescription ||
            userData.situationDescription ||
            userData.description ||
            '',
        };
      } catch (error) {
        leadData = fallbackLead;
      }
    }

    await sendLeadToZapier(leadData);
    window.location.href = `tel:${FIRM_INFO.phone}`;
  };

  const handleEmailAttorney = () => {
    navigate('/contact-flow', {
      state: {
        scenario: t.dashboard.contactAttorneyTitle,
      },
    });
  };

  const handleBlogClick = () => {
    window.open(FIRM_INFO.blogUrl, '_blank', 'noopener,noreferrer');
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/login', { replace: true });
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  return (
    <div className="page-container">
      <div className="page-content">
        <div className="rights-section">
          <button className="back-button" onClick={() => navigate('/')}>
            {t.dashboard.back}
          </button>
          <button onClick={handleLogout}>{t.dashboard.logout}</button>

          <h2>{t.dashboard.title}</h2>
          <p className="rights-intro">{t.dashboard.subtitle}</p>

          <div className="accordions-container">
            {SCENARIOS.map((scenario) => (
              <div key={scenario.id} className="accordion-item">
                <button className="accordion-header" onClick={() => navigate(scenario.route)}>
                  <span className="accordion-title">{scenario.title}</span>
                  <span className="accordion-toggle">→</span>
                </button>
                <div className="accordion-content">
                  <ul className="rights-list">
                    <li>{scenario.description}</li>
                  </ul>
                </div>
              </div>
            ))}
          </div>

          <section className="contact-section">
            <h3>{t.dashboard.contactTitle}</h3>
            <p>{t.dashboard.contactDesc}</p>

            <div className="action-buttons">
              <button className="btn btn-primary btn-large" onClick={handleCallAttorney}>
                {t.dashboard.getHelp}
              </button>
              <button className="btn btn-secondary btn-large" onClick={handleEmailAttorney}>
                {t.dashboard.emailAttorney}
              </button>
            </div>

            <div className="learn-more-link">
              <button className="link-button" onClick={handleBlogClick}>
                {t.dashboard.learnMore}
              </button>
            </div>
          </section>

          <section className="firm-info-footer">
            <h3>{FIRM_INFO.name}</h3>
            <p>📞 {FIRM_INFO.displayPhone}</p>
            <p>📧 {FIRM_INFO.email}</p>
            <p>📍 {FIRM_INFO.address}</p>
            <div className="footer-links">
              <Link to="/privacy" className="footer-link">
                {t.legal.privacy}
              </Link>
              <Link to="/terms" className="footer-link">
                {t.legal.terms}
              </Link>
            </div>
            <p className="legal-disclaimer">{t.legal.disclaimer}</p>
          </section>
        </div>
      </div>
    </div>
  );
}

export default KnowYourRights;
